import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ConfirmDialog, ui, fmtDate, asList,
} from '../components/adminUi';
import Icon from '../components/Icon';
import { formatPhone } from '../lib/phone';
import { orderStatusLabel } from '../lib/orderStatus';

const STATUS_LABEL = { open: 'Ochiq', pending: 'Kutilmoqda', resolved: 'Hal qilingan', closed: 'Yopilgan', rejected: 'Rad etilgan' };
const statusLabel = (st) => STATUS_LABEL[st] || st || '—';
const STATUS_TONE = { open: 'amber', pending: 'amber', resolved: 'green', closed: 'gray', rejected: 'red' };

// Qaror (decision) yorliqlari va tasdiqlash oynasidagi izohlar. Backend sinonimlarni
// ('client'|'mijoz'|'refund' / 'master'|'provider'|'usta') bir xil qabul qiladi.
const CLIENT_DECISIONS = ['client', 'mijoz', 'customer', 'refund'];
const MASTER_DECISIONS = ['master', 'provider', 'usta'];
const DECISION_LABEL = { client: 'Mijoz foydasiga', master: 'Usta foydasiga', rejected: 'Rad etildi' };
const canonicalDecision = (d) => {
  const v = String(d || '').trim().toLowerCase();
  if (CLIENT_DECISIONS.includes(v)) return 'client';
  if (MASTER_DECISIONS.includes(v)) return 'master';
  return v;
};
const decisionLabel = (d) => (d ? DECISION_LABEL[canonicalDecision(d)] || d : '');
const decisionNote = (d) => {
  const c = canonicalDecision(d);
  if (c === 'client') return "Mijoz foydasiga — buyurtma bekor qilinadi va to'lov qaytariladi.";
  // Qaror tanlanmasa backend buyurtmani yakunlaydi (usta foydasiga) — izoh ham shuni aytadi.
  return "Usta foydasiga — buyurtma yakunlanadi.";
};

// Tomonlar: backend clientName/clientPhone (+ client{}) va masterName/masterPhone (+ master{}) qaytaradi.
const partyOf = (row, key) => {
  const o = row?.[key] || {};
  const name = row?.[`${key}Name`] || o.name || null;
  const phone = row?.[`${key}Phone`] || o.phone || null;
  return { name, phone };
};
const partyText = ({ name, phone }) => (name ? `${name}${phone ? ` · ${formatPhone(phone)}` : ''}` : null);

export default function DisputesPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [decision, setDecision] = useState('');
  const [resolution, setResolution] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirming, setConfirming] = useState(false);
  // Hal qilingandan keyingi javob: { status, decision, taskStatus } — modalda ko'rsatiladi.
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch('/api/admin/disputes', auth.token);
      if (!res.ok) throw new Error();
      setItems(asList(await res.json()));
    } catch {
      setItems([]);
      setFetchError('Nizolarni yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setFormError('');
    setDecision('');
    setResolution('');
    setResult(null);
    setConfirming(false);
    setDetail({ ...row, __loading: true });
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/api/admin/disputes/${row.id}`, auth.token);
      if (res.ok) {
        setDetail(await res.json());
      } else {
        // Fall back to the list row if detail endpoint has no richer data.
        setDetail(row);
      }
    } catch {
      setDetail(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const isResolved = (row) => ['resolved', 'closed', 'rejected'].includes(row?.status);

  const askConfirm = () => {
    if (!detail) return;
    setFormError('');
    if (!resolution.trim()) { setFormError('Yechim matni majburiy'); return; }
    setConfirming(true);
  };

  const resolve = async () => {
    if (!detail) return;
    setFormError('');
    setSaving(true);
    try {
      const res = await apiFetch(`/api/admin/disputes/${detail.id}/resolve`, auth.token, {
        method: 'POST',
        body: JSON.stringify({
          resolution: resolution.trim(),
          ...(decision ? { decision } : {}),
        }),
      });
      const dd = await res.json().catch(() => ({}));
      if (!res.ok) {
        setConfirming(false);
        setFormError(dd.message || 'Hal qilishda xatolik yuz berdi.');
        return;
      }
      const out = dd.data || dd;
      const decided = out.decision ?? (decision || null);
      setResult({
        status: out.status || 'resolved',
        decision: decided,
        taskStatus: out.taskStatus ?? null,
      });
      setDetail((d) => ({
        ...d, status: out.status || 'resolved', decision: decided, resolution: resolution.trim(),
        taskStatus: out.taskStatus ?? d.taskStatus,
      }));
      setConfirming(false);
      await load();
    } catch {
      setConfirming(false);
      setFormError('Tarmoq xatosi. Qayta urinib ko\'ring.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <PageHeader title="Nizolar" subtitle="Nizolarni ko'rib chiqish va hal qilish">
        <button style={ui.pagerBtn} onClick={load}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (fetchError ? null : (
        <Empty icon="scale" title="Hozircha nizolar yo'q" hint="Buyurtma bo'yicha ochilgan nizolar bu yerda ko'rinadi. Foydalanuvchi shikoyatlari «Shikoyatlar» bo'limida." actionLabel="Yangilash" onAction={load} />
      )) : (
        <div style={ui.tableWrap}>
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={{ ...ui.th, width: '48px' }}>#</th>
                <th style={ui.th}>Buyurtma</th>
                <th style={ui.th}>Mavzu / sabab</th>
                <th style={ui.th}>Mijoz va usta</th>
                <th style={ui.th}>Holat</th>
                <th style={ui.th}>Yaratilgan</th>
                <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} style={ui.tr}>
                  <td style={{ ...ui.td, color: 'var(--color-text-muted)' }}>{r.id}</td>
                  <td style={{ ...ui.td, fontWeight: 600, color: 'var(--color-text)' }}>
                    {(r.orderId ?? r.taskId) != null ? `#${r.orderId ?? r.taskId}` : '—'}
                  </td>
                  <td style={{ ...ui.td, color: 'var(--color-text)' }}>
                    {r.subject || r.reason || r.title || r.description || `Nizo #${r.id}`}
                  </td>
                  <td style={ui.td}>
                    {[['Mijoz', partyOf(r, 'client')], ['Usta', partyOf(r, 'master')]].map(([label, p]) => (
                      <div key={label} style={{ fontSize: '13px' }}>
                        <span style={{ color: 'var(--color-text-muted)' }}>{label}: </span>
                        {partyText(p) || '—'}
                      </div>
                    ))}
                  </td>
                  <td style={ui.td}>
                    <Badge tone={STATUS_TONE[r.status] || 'gray'}>{statusLabel(r.status)}</Badge>
                  </td>
                  <td style={ui.td}>{fmtDate(r.createdAt)}</td>
                  <td style={{ ...ui.td, textAlign: 'right' }}>
                    <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openDetail(r)}>
                      Batafsil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <Modal title={`Nizo #${detail.id}`} onClose={() => { setDetail(null); setResult(null); }} maxWidth="500px" busy={saving || confirming}>
          {detailLoading || detail.__loading ? (
            <Loading />
          ) : (
            <>
              <Row label="Mavzu" value={detail.subject || detail.title || detail.reason} />
              <Row label="Tavsif" value={detail.description || detail.message} />
              <Row label="Holat" value={statusLabel(detail.status)} />
              <Row label="Buyurtma ID" value={detail.orderId ?? detail.taskId} />
              <Row label="Mijoz" value={partyText(partyOf(detail, 'client'))} />
              <Row label="Usta" value={partyText(partyOf(detail, 'master'))} />
              {detail.taskStatus && <Row label="Buyurtma holati" value={orderStatusLabel(detail.taskStatus)} />}
              {detail.decision && <Row label="Qaror" value={decisionLabel(detail.decision)} />}
              <Row label="Yaratilgan" value={fmtDate(detail.createdAt, true)} />
              {detail.resolution && <Row label="Yechim" value={detail.resolution} />}

              {isResolved(detail) ? (
                <div style={{ ...ui.infoBanner, marginTop: '12px' }}>
                  {result ? 'Nizo hal qilindi.' : 'Bu nizo allaqachon hal qilingan.'}
                  {result && result.decision ? ` ${decisionNote(result.decision)}` : ''}
                </div>
              ) : (
                <>
                  <div style={{ height: '8px' }} />
                  <div style={ui.field}>
                    <label style={ui.label}>Qaror (decision)</label>
                    <select
                      style={ui.select}
                      value={decision}
                      onChange={(e) => setDecision(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">Tanlang (ixtiyoriy)</option>
                      <option value="client">Mijoz foydasiga</option>
                      <option value="provider">Usta foydasiga</option>
                      <option value="refund">Pulni qaytarish</option>
                      <option value="rejected">Rad etish</option>
                    </select>
                  </div>
                  <div style={ui.field}>
                    <label style={ui.label}>Yechim matni <span style={ui.req}>*</span></label>
                    <textarea
                      style={ui.textarea}
                      rows={3}
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      placeholder="Qanday hal qilinganini yozing"
                      disabled={saving}
                    />
                  </div>
                  {formError && <div style={ui.formError}>{formError}</div>}
                  <div style={ui.modalActions}>
                    <button style={ui.cancelBtn} onClick={() => setDetail(null)} disabled={saving}>
                      Yopish
                    </button>
                    <button
                      style={{ ...ui.saveBtn, ...(saving ? ui.btnDisabled : {}) }}
                      onClick={askConfirm}
                      disabled={saving}
                    >
                      {saving ? 'Saqlanmoqda...' : 'Hal qilish'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </Modal>
      )}

      {detail && confirming && (
        <ConfirmDialog
          title="Nizoni hal qilishni tasdiqlang"
          message={decisionNote(decision)}
          confirmLabel="Tasdiqlash"
          danger={canonicalDecision(decision) === 'client'}
          busy={saving}
          onConfirm={resolve}
          onCancel={() => setConfirming(false)}
        />
      )}
    </AdminLayout>
  );
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '12px',
      padding: '8px 0', borderBottom: '1px solid var(--color-border)', fontSize: '14px',
    }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--color-text)', fontWeight: '500', textAlign: 'right' }}>{value}</span>
    </div>
  );
}
